import { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, Download, CheckCheck, X, MoreHorizontal, RotateCcw, CalendarDays, ExternalLink } from 'lucide-react';
import { paymentReconApi } from '../../services/paymentReconApi';
import { useToast } from '../ui/Toast';
import { ConfirmModal } from '../ui/ConfirmModal';
import { ContextMenu, type ContextMenuItem } from '../ui/ContextMenu';
import { useTableSort } from '../../hooks/useTableSort';
import { SortableTh } from './SortableTh';
import { formatMoney, formatDate, statusBadgeClass, agingClass } from '../../utils/paymentReconFormat';
import { CommentBadge } from './CommentBadge';
import type { CommentTarget } from './InvoiceCommentsPanel';
import type { ReconInvoice } from '../../types/paymentRecon';

interface Props {
    canEdit: boolean;
    companyCode: string;
    /** Drill-down filter carried in from the Aging tab (customer + optional age bucket). */
    initialFilter?: { customerCode: string | null; customerName: string | null; status: string; bucket: string | null } | null;
    onChanged: () => void;
    /**
     * Jump to the Payments tab. Pass the settling transaction id to open that payment's
     * match drawer straight away — a bank-matched invoice is corrected there (swap or
     * remove the allocation), and without the id the user cannot find the right payment.
     */
    onGotoPayments?: (transactionId?: number) => void;
    /** Open the per-invoice comments side-panel. */
    onOpenComments: (target: CommentTarget) => void;
    /** Bumped by the page after a comment change so count badges re-fetch. */
    commentsReloadKey?: number;
}

const BUCKET_LABELS: Record<string, string> = { current: 'Current', '1-30': '1–30', '31-60': '31–60', '61-90': '61–90', '90+': '90+' };

/** Does an invoice's aging bucket match the drilled-into bucket? ('current' also catches undated.) */
function inBucket(agingBucket: string, bucket: string): boolean {
    if (bucket === 'current') return agingBucket === 'current' || agingBucket === 'unknown';
    return agingBucket === bucket;
}

/** Local (CET) yyyy-mm-dd — never toISOString (UTC shift would roll the date). */
function todayIso(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const STATUS_OPTIONS = [
    { value: 'open', label: 'Open' },
    { value: 'partial', label: 'Partially paid' },
    { value: 'paid', label: 'Paid' },
    { value: 'all', label: 'All' },
];

export function InvoicesTab({ canEdit, companyCode, initialFilter, onChanged, onGotoPayments, onOpenComments, commentsReloadKey }: Props) {
    const { toast } = useToast();
    const [invoices, setInvoices] = useState<ReconInvoice[]>([]);
    const [status, setStatus] = useState(initialFilter?.status ?? 'open');
    // A customer drill-down filters by code when available, else by name via the search box.
    const [q, setQ] = useState(initialFilter && !initialFilter.customerCode && initialFilter.customerName ? initialFilter.customerName : '');
    const [customerCode] = useState<string | null>(initialFilter?.customerCode ?? null);
    const [customerLabel, setCustomerLabel] = useState<string | null>(initialFilter?.customerName ?? null);
    const [customerActive, setCustomerActive] = useState<boolean>(!!initialFilter);
    const [bucketFilter, setBucketFilter] = useState<string | null>(initialFilter?.bucket ?? null);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [confirmPaid, setConfirmPaid] = useState(false);
    const [paidDate, setPaidDate] = useState(todayIso);
    // Per-row action menu (kebab) + the two actions it can trigger.
    const [menu, setMenu] = useState<{ inv: ReconInvoice; x: number; y: number } | null>(null);
    const [confirmReopen, setConfirmReopen] = useState<{ ids: number[]; label: string } | null>(null);
    const [editDateFor, setEditDateFor] = useState<ReconInvoice | null>(null);
    const [editDateValue, setEditDateValue] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await paymentReconApi.listInvoices({
                companyCode, status, q,
                customerCode: customerActive && customerCode ? customerCode : undefined,
                limit: 2000,
            });
            setInvoices(res.invoices);
            setSelected(new Set());
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to load invoices', 'error');
        } finally {
            setLoading(false);
        }
    }, [companyCode, status, q, customerActive, customerCode, toast]);

    const clearCustomerFilter = () => {
        setCustomerActive(false);
        setCustomerLabel(null);
        setBucketFilter(null);
        if (initialFilter && !initialFilter.customerCode) setQ('');  // name-based filter lived in the search box
    };

    useEffect(() => {
        void commentsReloadKey; // re-fetch comment-count badges after a comment change
        void load();
    }, [load, commentsReloadKey]);

    const shown = useMemo(
        () => (customerActive && bucketFilter ? invoices.filter(i => inBucket(i.AgingBucket, bucketFilter)) : invoices),
        [invoices, customerActive, bucketFilter],
    );

    const { sorted, sort, toggleSort } = useTableSort(shown, {
        invoice: r => r.InvoiceNo,
        date: r => r.InvoiceDate,
        customer: r => r.CustomerName,
        due: r => r.DueDate,
        gross: r => r.GrossAmount,
        open: r => r.OpenAmount,
        aging: r => r.DaysOverdue,
        status: r => r.Status,
        paid: r => r.PaidDate,
        via: r => r.PaidMethod,
    });

    const toggle = (id: number) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const doMarkPaid = async () => {
        const count = selected.size;
        setConfirmPaid(false);
        if (count === 0) return;
        try {
            await paymentReconApi.markInvoicesPaid([...selected], true, paidDate);
            toast(`${count} invoice(s) marked paid`, 'success');
            void load();
            onChanged();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed', 'error');
        }
    };

    // Re-open / date-edit only apply to MANUALLY-paid invoices (bank payments are
    // reverted via the Payments tab → Unmatch). IDs of the manual-paid selection:
    const selectedManualPaidIds = useMemo(
        () => sorted.filter(r => selected.has(r.Id) && r.PaidMethod === 'manual').map(r => r.Id),
        [sorted, selected],
    );

    const openRowMenu = (e: React.MouseEvent, inv: ReconInvoice) => {
        e.stopPropagation();
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setMenu({ inv, x: rect.right, y: rect.bottom + 4 });
    };

    const menuItems = (inv: ReconInvoice): ContextMenuItem[] => {
        if (inv.PaidMethod === 'manual') {
            return [
                {
                    label: 'Edit payment date',
                    icon: <CalendarDays className="h-4 w-4" />,
                    onClick: () => { setEditDateFor(inv); setEditDateValue(inv.PaidDate ?? todayIso()); },
                },
                {
                    label: 'Re-open invoice',
                    icon: <RotateCcw className="h-4 w-4" />,
                    onClick: () => setConfirmReopen({ ids: [inv.Id], label: inv.InvoiceNo }),
                },
            ];
        }
        // Bank-matched: send the user straight to the payment that holds the allocation,
        // named so they can see it is the right one before anything is changed.
        const paidBy = inv.PaidTransactionId;
        return [
            {
                label: paidBy
                    ? `Open payment ${formatDate(inv.PaidTransactionDate)} · ${formatMoney(inv.PaidTransactionAmount ?? 0)}`
                    : 'Resolve via Payments (Unmatch)',
                icon: <ExternalLink className="h-4 w-4" />,
                onClick: () => onGotoPayments?.(paidBy ?? undefined),
            },
        ];
    };

    const doReopen = async () => {
        if (!confirmReopen) return;
        const { ids } = confirmReopen;
        setConfirmReopen(null);
        try {
            await paymentReconApi.markInvoicesPaid(ids, false);
            toast(`${ids.length} invoice(s) re-opened`, 'success');
            void load();
            onChanged();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed', 'error');
        }
    };

    const saveEditedDate = async () => {
        if (!editDateFor || !/^\d{4}-\d{2}-\d{2}$/.test(editDateValue)) return;
        const inv = editDateFor;
        setEditDateFor(null);
        try {
            await paymentReconApi.markInvoicesPaid([inv.Id], true, editDateValue);
            toast(`Payment date updated for ${inv.InvoiceNo}`, 'success');
            void load();
            onChanged();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed', 'error');
        }
    };

    return (
        <div>
            <div className="mb-3 flex flex-wrap items-center gap-2">
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm">
                    {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') void load(); }}
                        placeholder="Search invoice no / customer…"
                        className="w-full rounded-md border border-slate-300 py-1.5 pl-8 pr-3 text-sm"
                    />
                </div>
                {customerActive && customerLabel && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
                        {customerLabel}{bucketFilter ? ` · ${BUCKET_LABELS[bucketFilter] ?? bucketFilter}` : ''}
                        <button onClick={clearCustomerFilter} title="Clear filter" className="rounded-full p-0.5 hover:bg-amber-100"><X className="h-3 w-3" /></button>
                    </span>
                )}
                {canEdit && selected.size > 0 && (
                    <div className="flex items-center gap-2">
                        <label className="flex items-center gap-1 text-xs text-slate-500">
                            Settled on
                            <input type="date" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} className="rounded border border-slate-300 px-2 py-1 text-sm" />
                        </label>
                        <button onClick={() => setConfirmPaid(true)} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700">
                            <CheckCheck className="h-4 w-4" /> Mark {selected.size} paid
                        </button>
                        {selectedManualPaidIds.length > 0 && (
                            <button
                                onClick={() => setConfirmReopen({ ids: selectedManualPaidIds, label: `${selectedManualPaidIds.length} invoice(s)` })}
                                className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-700 hover:bg-amber-50"
                            >
                                <RotateCcw className="h-4 w-4" /> Re-open {selectedManualPaidIds.length}
                            </button>
                        )}
                    </div>
                )}
                <a
                    href={paymentReconApi.exportUrl('invoices', companyCode)}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
                >
                    <Download className="h-4 w-4" /> Excel
                </a>
            </div>

            {loading ? (
                <div className="py-10 text-center text-sm text-slate-400">Loading…</div>
            ) : sorted.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400">No invoices in this view.</div>
            ) : (
                <div className="max-h-[65vh] overflow-auto rounded-lg border border-slate-200">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 [&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-slate-50">
                            <tr>
                                {canEdit && <th className="px-3 py-2 w-8"></th>}
                                <SortableTh label="Invoice" sortKey="invoice" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Invoice date" sortKey="date" sort={sort} onSort={toggleSort} className="whitespace-nowrap" />
                                <SortableTh label="Customer" sortKey="customer" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Due" sortKey="due" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Gross" sortKey="gross" sort={sort} onSort={toggleSort} className="text-right" />
                                <SortableTh label="Open" sortKey="open" sort={sort} onSort={toggleSort} className="text-right" />
                                <SortableTh label="Aging" sortKey="aging" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Status" sortKey="status" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Paid on" sortKey="paid" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Via" sortKey="via" sort={sort} onSort={toggleSort} />
                                {canEdit && <th className="px-3 py-2 w-8"></th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {sorted.map((inv) => (
                                <tr
                                    key={inv.Id}
                                    onClick={() => onOpenComments({ invoiceId: inv.Id, invoiceNo: inv.InvoiceNo, customerName: inv.CustomerName })}
                                    className="cursor-pointer hover:bg-slate-50"
                                >
                                    {canEdit && (
                                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                                            <input type="checkbox" checked={selected.has(inv.Id)} onChange={() => toggle(inv.Id)} />
                                        </td>
                                    )}
                                    <td className="whitespace-nowrap px-3 py-2 font-medium text-slate-700">
                                        <span className="inline-flex items-center gap-1.5">
                                            {inv.InvoiceNo}
                                            {inv.DocumentType === 'CreditMemo' && <span className="rounded bg-emerald-50 px-1 text-xs text-emerald-700">CM</span>}
                                            <CommentBadge count={inv.CommentCount} />
                                        </span>
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2 text-slate-500">{formatDate(inv.InvoiceDate)}</td>
                                    <td className="max-w-xs truncate px-3 py-2 text-slate-600" title={inv.CustomerName ?? ''}>{inv.CustomerName}</td>
                                    <td className="whitespace-nowrap px-3 py-2 text-slate-500">{formatDate(inv.DueDate)}</td>
                                    <td className="whitespace-nowrap px-3 py-2 text-right text-slate-700">{formatMoney(inv.GrossAmount, inv.Currency)}</td>
                                    <td className="whitespace-nowrap px-3 py-2 text-right font-medium text-slate-800">{formatMoney(inv.OpenAmount, inv.Currency)}</td>
                                    <td className={`whitespace-nowrap px-3 py-2 text-xs ${agingClass(inv.AgingBucket)}`}>
                                        {inv.AgingBucket === 'current' ? 'current' : inv.AgingBucket === 'paid' ? '–' : `${inv.DaysOverdue ?? ''}d`}
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(inv.Status)}`}>{inv.Status}</span>
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2 text-slate-500">{inv.PaidDate ? formatDate(inv.PaidDate) : <span className="text-slate-300">–</span>}</td>
                                    <td className="px-3 py-2">
                                        {inv.PaidMethod
                                            ? <span
                                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${inv.PaidMethod === 'bank' ? 'bg-sky-100 text-sky-700' : 'bg-violet-100 text-violet-700'}`}
                                                title={inv.PaidMethod === 'bank' && inv.PaidTransactionId
                                                    ? `Settled by the payment of ${formatDate(inv.PaidTransactionDate)} · ${formatMoney(inv.PaidTransactionAmount ?? 0)}\n${inv.PaidTransactionRef ?? ''}`
                                                    : undefined}
                                              >{inv.PaidMethod}</span>
                                            : <span className="text-slate-300">–</span>}
                                    </td>
                                    {canEdit && (
                                        <td className="px-3 py-2 text-right">
                                            {inv.PaidMethod && (
                                                <button
                                                    onClick={(e) => openRowMenu(e, inv)}
                                                    title="Actions"
                                                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                                                >
                                                    <MoreHorizontal className="h-4 w-4" />
                                                </button>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {confirmPaid && (
                <ConfirmModal
                    title="Mark invoices as paid"
                    message={`Mark ${selected.size} invoice(s) as paid, settled on ${paidDate} (backlog cleanup)? This is auditable and reversible.`}
                    confirmLabel="Mark paid"
                    variant="warning"
                    onConfirm={doMarkPaid}
                    onCancel={() => setConfirmPaid(false)}
                />
            )}

            {menu && (
                <ContextMenu x={menu.x} y={menu.y} items={menuItems(menu.inv)} onClose={() => setMenu(null)} />
            )}

            {confirmReopen && (
                <ConfirmModal
                    title="Re-open invoice(s)"
                    message={`Re-open ${confirmReopen.label} and clear the manual payment mark? The invoice(s) return to Open / Partially paid. This is auditable and reversible.`}
                    confirmLabel="Re-open"
                    variant="warning"
                    onConfirm={doReopen}
                    onCancel={() => setConfirmReopen(null)}
                />
            )}

            {editDateFor && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setEditDateFor(null)}>
                    <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-slate-800">Edit payment date</h3>
                        <p className="mt-1 text-sm text-slate-600">{editDateFor.InvoiceNo} · {editDateFor.CustomerName}</p>
                        <label className="mt-4 block text-xs font-medium text-slate-500">Settled on</label>
                        <input
                            type="date"
                            value={editDateValue}
                            onChange={(e) => setEditDateValue(e.target.value)}
                            className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                        />
                        <div className="mt-5 flex justify-end gap-2">
                            <button onClick={() => setEditDateFor(null)} className="rounded-md border px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
                            <button
                                onClick={saveEditedDate}
                                disabled={!/^\d{4}-\d{2}-\d{2}$/.test(editDateValue)}
                                className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
